/*
  © 2025 TeamDrop
  https://www.teamdrop.com.br
*/

class Navbar extends HTMLElement {
  constructor () {
    super()

    this.handleDropdownsBackdrop()
    this.handleHeaderReveal()
    this.adjustOffcanvasMenu()
  }

  adjustOffcanvasMenu () {
    const offcanvasMenu = document.querySelector('#offcanvas-menu')
    const navbarMobile = document.querySelector('#navbar-mobile')
    const toggler = navbarMobile.querySelector('[aria-controls="offcanvas-menu"]')

    offcanvasMenu?.addEventListener('show.bs.offcanvas', () => {
      const navbarSpacing = document.querySelector('#navbar-mobile').getBoundingClientRect().bottom
      offcanvasMenu.style.height = `${window.innerHeight - navbarSpacing + 1}px`
      toggler.classList.add('svg-icon-menu-close')

      this.insertAdjacentHTML('beforeBegin', `
        <style>
          #offcanvas-menu + .offcanvas-backdrop {
            top: ${navbarSpacing}px
          }
        </style>
      `)
    })

    offcanvasMenu?.addEventListener('hide.bs.offcanvas', () => {
      toggler.classList.remove('svg-icon-menu-close')
    })
  }

  handleHeaderReveal () {
    let oldScroll = window.scrollY
    const headerGroup = document.querySelector('#header-group')

    window.addEventListener('scroll', () => {
      const newScroll = window.scrollY
      if (newScroll > oldScroll) {
        if (newScroll > headerGroup.clientHeight) {
          headerGroup.classList.add('hide')
        }
      } else if (newScroll < oldScroll) {
        headerGroup.classList.remove('hide')
      }

      oldScroll = Math.max(window.scrollY, 0)
    })
  }

  handleDropdownsBackdrop () {
    this.querySelectorAll('#navbar-desktop .dropdown-toggle').forEach(dropdown => {
      dropdown.addEventListener('hidden.bs.dropdown', () => {
        if (!document.querySelector('#navbar-desktop .dropdown-toggle.show')) {
          document.body.classList.remove('navbar-dropdown-open')
        }
      })
      dropdown.addEventListener('show.bs.dropdown', () => {
        document.body.classList.add('navbar-dropdown-open')
      })
    })
  }
}
customElements.define('navbar-wrapper', Navbar)

class Marquee extends HTMLElement {
  constructor () {
    super()

    this.init()

    document.addEventListener('shopify:section:load', (event) => {
      if (event.detail.sectionId === this.dataset.sectionId) {
        this.init()
      }
    })
  }

  init () {
    const list = this.querySelector('.marquee-list')
    list.querySelectorAll('.marquee-item[aria-hidden="true"]').forEach(elem => elem.remove())

    const marqueeWidth = list.scrollWidth
    const marqueeLength = list.querySelectorAll('li').length

    list.insertAdjacentHTML('beforeEnd', list.innerHTML)
    list.insertAdjacentHTML('beforeEnd', list.innerHTML)

    list.querySelectorAll('.marquee-item').forEach((item, index) => {
      if (index >= marqueeLength) {
        item.setAttribute('aria-hidden', 'true')
      }
    })

    let style = `
      <style>
        #marquee-${this.dataset.marqueeId} .marquee-list {
          animation-name: marquee_animation_${this.dataset.marqueeId};
          animation-duration: ${this.dataset.marqueeDuration}s;
        }
        @keyframes marquee_animation_${this.dataset.marqueeId} {
          to { transform: translateX(-${marqueeWidth}px); }
        }
      </style>
    `
    if (this.dataset.marqueeDirection === 'right') {
      style += `
        <style>
          @keyframes marquee_animation_${this.dataset.marqueeId} {
            from { transform: translateX(-${marqueeWidth}px); }    
            to { transform: 0); }    
          }
        </style>
      `
    }

    this.insertAdjacentHTML('beforeBegin', style)
  }
}
customElements.define('marquee-container', Marquee)

class NewsletterPopup extends HTMLElement {
  constructor () {
    super()

    if (window.Shopify.designMode) return

    if (window.location.href.includes('newsletter-popup')) {
      window.bootstrap.Modal.getOrCreateInstance(this).show()
      return
    }

    if (document.cookie.indexOf('td-newsletter-popup') > -1) {
      return
    };

    (async () => {
      await new Promise(resolve => setTimeout(resolve, Number(this.dataset.configDelay) * 1000))
      window.theme.createCookie('td-newsletter-popup', true, Number(this.dataset.configDaysToWait))
      window.bootstrap.Modal.getOrCreateInstance(this).show()
    })()
  }
}
customElements.define('newsletter-popup', NewsletterPopup)


class ParallaxMedia extends HTMLElement {
  constructor () {
    super()

    this.animateOnScroll()

    const script = document.querySelector('script[src*="vendor-simple-parallax"]')
    script.onload = () => {
      this.init()
    }

    document.addEventListener('shopify:section:load', (event) => {
      if (event.detail.sectionId === this.dataset.sectionId) {
        this.init()
      }
    })
  }

  init () {
    new window.simpleParallax(this.querySelectorAll('.parallax-media-media'), {
      orientation: this.dataset.parallaxOrientation,
      scale: Number(this.dataset.parallaxScale)
    })
  }

  animateOnScroll () {
    window.addEventListener('scroll', () => {
      const windowHeight = window.innerHeight
      const sectionHeight = this.clientHeight

      const top = Math.round(this.getBoundingClientRect().top)
      const bottom = Math.round(this.getBoundingClientRect().bottom)
      const visibilityTop = Math.round((windowHeight - top) / sectionHeight * 100)
      const visibilityBottom = Math.round(bottom / sectionHeight * 100)

      if (visibilityTop > 0 && visibilityTop < 100) {
        this.querySelectorAll('.media-wrapper').forEach(elem => {
          const overlayOpacity = Number(elem.dataset.overlayOpacity)
          const final = Math.round((100 - overlayOpacity) * (1 - visibilityTop / 100) + overlayOpacity)
          elem.style.setProperty('--overlay-opacity', final + '%')
        })
      } else if (visibilityBottom > 0 && visibilityBottom < 100) {
        this.querySelectorAll('.media-wrapper').forEach(elem => {
          const overlayOpacity = Number(elem.dataset.overlayOpacity)
          const final = Math.round((100 - overlayOpacity) * (1 - visibilityBottom / 100) + overlayOpacity)
          elem.style.setProperty('--overlay-opacity', final + '%')
        })
      }
    })
  }
}
customElements.define('parallax-media', ParallaxMedia)

class ShoppableVideos extends HTMLElement {
  constructor () {
    super()

    this.querySelectorAll('video').forEach(video => {
      video.addEventListener('click', () => {
        this.onVideoPlay(video)
      })
    })

    this.querySelectorAll('a[data-quick-view]').forEach(link => {
      link.addEventListener('click', (event) => {
        event.preventDefault()
        this.openQuickView(link)
      })
    })

    this.querySelector('.swiper').swiper.on('slideChange', () => {
      this.querySelectorAll('video').forEach(video => {
        if (video.closest('.card').dataset.playing === 'true') {
          video.closest('.card').setAttribute('data-playing', 'false')
          video.pause()
        }
      })
    })
  }

  onVideoPlay (video) {
    this.querySelectorAll('video').forEach(elem => {
      if (elem.dataset.index !== video.dataset.index) {
        elem.closest('.card').setAttribute('data-playing', 'false')
        elem.pause()
      }
    })

    const card = video.closest('.card')

    if (card.dataset.playing === 'true') {
      video.closest('.card').setAttribute('data-playing', 'false')
      video.pause()
    } else {
      video.closest('.card').setAttribute('data-playing', 'true')
      video.muted = false
      video.play()
    }
  }

  async openQuickView (link) {
    link.disabled = true
    link.setAttribute('aria-busy', 'true')
    link.querySelector('.icon-wrapper').setAttribute('hidden', 'hidden')
    link.querySelector('.spinner-border').removeAttribute('hidden')

    const buildProductJsonUrl = (rawUrl) => {
      try {
        const url = new URL(rawUrl, window.location.origin)
        if (!url.pathname.endsWith('.js')) {
          url.pathname = url.pathname.replace(/\/$/, '') + '.js'
        }
        return url.toString()
      } catch (e) {
        const parts = String(rawUrl || '').split(/([?#].*)/)
        const base = (parts[0] || '').replace(/\/$/, '') + '.js'
        const tail = parts[1] || ''
        return base + tail
      }
    }

    const response = await fetch(buildProductJsonUrl(link.dataset.productUrl))

    if (response.ok) {
      const data = await response.json()
      console.log(data)

      document.querySelector('product-quick-view-offcanvas').showModal(data)
    }

    link.disabled = false
    link.removeAttribute('aria-busy')
    link.querySelector('.icon-wrapper').removeAttribute('hidden')
    link.querySelector('.spinner-border').setAttribute('hidden', 'hidden')
  }
}
customElements.define('shoppable-videos', ShoppableVideos)


class StickyMedia extends HTMLElement {
  constructor () {
    super()

    this.animateOnScroll()
  }

  animateOnScroll () {
    window.addEventListener('scroll', () => {
      const windowHeight = window.innerHeight

      const top = this.getBoundingClientRect().top
      const bottom = this.getBoundingClientRect().bottom
      const visibilityTop = Math.round(top / windowHeight * 100)
      const visibilityBottom = Math.round(bottom / windowHeight * 100)

      if (visibilityTop > 0 && visibilityTop < 100) {
        this.querySelectorAll('.media-wrapper').forEach(elem => {
          const overlayOpacity = Number(elem.dataset.overlayOpacity)
          const final = Math.round((100 - overlayOpacity) * visibilityTop / 100 + overlayOpacity)
          elem.style.setProperty('--overlay-opacity', final + '%')
        })
      } else if (visibilityBottom > 0 && visibilityBottom < 100) {
        this.querySelectorAll('.media-wrapper').forEach(elem => {
          const overlayOpacity = Number(elem.dataset.overlayOpacity)
          const final = Math.round((100 - overlayOpacity) * (1 - visibilityBottom / 100) + overlayOpacity)
          elem.style.setProperty('--overlay-opacity', final + '%')
        })
      }
    })
  }
}
customElements.define('sticky-media', StickyMedia)

class MyImageCompare extends HTMLElement {
  constructor () {
    super()

    const script = document.querySelector('script[src*="vendor-image-compare"]')
    script.onload = () => {
      this.init()
    }

    document.addEventListener('shopify:section:load', (event) => {
      if (event.detail.sectionId === this.dataset.sectionId) {
        this.init()
      }
    })
  }

  init () {
    this.querySelectorAll('[data-image-compare]').forEach(elem => {
      new window.ImageCompare(elem, {
        controlColor: this.dataset.icvControlsColor,
        controlShadow: true,
        addCircle: this.dataset.icvControlsCircle === 'true',
        addCircleBlur: true,
        showLabels: this.dataset.icvShowLabels === 'true',
        labelOptions: {
          before: this.dataset.icvLabelBefore,
          after: this.dataset.icvLabelAfter,
          onHover: false
        },
        smoothing: this.dataset.icvSmoothing === 'true',
        smoothingAmount: 100,
        hoverStart: this.dataset.icvHoverStart === 'true',
        verticalMode: this.dataset.icvVerticalMode === 'true',
        startingPoint: Number(this.dataset.icvStartingPoint),
        fluidMode: false
      }).mount()
    })
  }
}
customElements.define('image-compare', MyImageCompare)

class LookBook extends HTMLElement {
  constructor () {
    super()

    this.points = this.querySelectorAll('.lookbook-point-list button')
    this.mySwiper = this.querySelector('.swiper').swiper

    this.points.forEach(elem => {
      elem.addEventListener('click', () => this.onPointerClick(elem))
    })

    this.mySwiper.on('slideChange', () => this.onSlideChange())
  }

  onPointerClick (btn) {
    this.mySwiper.slideTo(Number(btn.dataset.index))
    this.scrollIntoView()
  }

  onSlideChange () {
    const selectedPoint = this.points[this.mySwiper.activeIndex]

    this.points.forEach(elem => {
      elem.classList.remove('active')
      if (elem.dataset.index !== selectedPoint.dataset.index) {
        window.bootstrap.Tooltip.getOrCreateInstance(elem).hide()
      }
    })

    selectedPoint.classList.add('active')

    if (!selectedPoint.getAttribute('aria-describedby')) {
      window.bootstrap.Tooltip.getOrCreateInstance(selectedPoint).show()
    }
  }
}
customElements.define('look-book', LookBook)

class CrossSells extends HTMLElement {
  constructor () {
    super()

    this.querySelectorAll('input[type="checkbox"').forEach(checkbox => {
      checkbox.addEventListener('change', () => this.onCheckboxChange(checkbox))
    })

    this.querySelectorAll('select[name="variant-id"').forEach(select => {
      select.addEventListener('change', () => this.onChangeVariant(select))
    })

    this.querySelectorAll('.btn-atc').forEach(btn => {
      btn.addEventListener('click', () => this.addToCart(btn))
    })
  }

  onChangeVariant (select) {
    const variantImage = select[select.selectedIndex].dataset.variantImage

    if (variantImage.length) {
      select.closest('.product-card').querySelector('.product-card-img-wrapper img').setAttribute('src', variantImage)
    }

    this.updateTotalPrice()
  }

  onCheckboxChange (checkbox) {
    if (checkbox.checked) {
      checkbox.closest('.product-card').setAttribute('data-is-selected', 'true')
    } else {
      checkbox.closest('.product-card').setAttribute('data-is-selected', 'false')
    }
    this.updateTotalPrice()
  }

  updateTotalPrice () {
    let totalPrice = 0
    let totalComparePrice = 0

    this.querySelectorAll('input[type="checkbox"').forEach(checkbox => {
      if (checkbox.checked) {
        const inputHidden = checkbox.closest('.product-card').querySelector('input[name="variant-id"][type="hidden"]')
        if (inputHidden) {
          totalPrice += Number(inputHidden.dataset.price)
          totalComparePrice += Number(inputHidden.dataset.compareAtPrice)
        }

        const select = checkbox.closest('.product-card').querySelector('select[name="variant-id"]')
        if (select) {
          totalPrice += Number(select[select.selectedIndex].dataset.price)
          totalComparePrice += Number(select[select.selectedIndex].dataset.compareAtPrice)
        }
      }
    })

    this.querySelectorAll('[data-total-price]').forEach(elem => {
      elem.textContent = `${window.Shopify.formatMoney(totalPrice)}`.replace('.00', '')
    })

    this.querySelectorAll('[data-total-compare-price]').forEach(elem => {
      elem.textContent = `${window.Shopify.formatMoney(totalComparePrice)}`.replace('.00', '')
    })

    this.querySelectorAll('[data-total-savings]').forEach(elem => {
      elem.textContent = `${window.Shopify.formatMoney(totalComparePrice - totalPrice)}`.replace('.00', '')
    })

    if (totalPrice === 0) {
      this.querySelector('.btn-atc').disabled = true
    } else {
      this.querySelector('.btn-atc').disabled = false
    }

    if (totalComparePrice > totalPrice) {
      this.querySelectorAll('[data-total-compare-price]').forEach(elem => {
        elem.closest('s').removeAttribute('hidden')
      })
      this.querySelectorAll('[data-total-savings]').forEach(elem => {
        elem.closest('.cross-sells-footer-total-savings').removeAttribute('hidden')
      })
    } else {
      this.querySelectorAll('[data-total-compare-price]').forEach(elem => {
        elem.closest('s').setAttribute('hidden', 'hidden')
      })
      this.querySelectorAll('[data-total-savings]').forEach(elem => {
        elem.closest('.cross-sells-footer-total-savings').setAttribute('hidden', 'hidden')
      })
    }
  }

  async addToCart (atcBtn) {
    atcBtn.style.width = `${atcBtn.clientWidth}px`
    atcBtn.classList.add('loading')
    atcBtn.disabled = true
    atcBtn.setAttribute('aria-busy', 'true')

    const items = []

    this.querySelectorAll('input[type="checkbox"').forEach(checkbox => {
      if (checkbox.checked) {
        const id = Number(checkbox.closest('.product-card').querySelector('[name="variant-id"]').value)

        items.push({
          id,
          quantity: 1
        })
      }
    })

    await document.querySelector('cart-container').add({ items })

    atcBtn.style.width = ''
    atcBtn.classList.remove('loading')
    atcBtn.disabled = false
    atcBtn.setAttribute('aria-busy', 'false')
  }
}
customElements.define('cross-sells', CrossSells)

class InstagramGallery extends HTMLElement {
  constructor () {
    super()

    this.gallery = new window.Swiper(this, {
      speed: Number(this.dataset.sliderSpeed),
      spaceBetween: 12,
      centeredSlides: true,
      autoplay: this.autoplay,
      loop: true,
      navigation: {
        prevEl: '.swiper-button-prev',
        nextEl: '.swiper-button-next'
      },
      mousewheel: {
        enabled: true,
        forceToAxis: true
      },
      breakpoints: {
        0: {
          slidesPerView: 1.4,
          spaceBetween: this.dataset.swiperSpacing === 'true' ? 8 : 0
        },
        600: { 
          slidesPerView: 2,
          spaceBetween: this.dataset.swiperSpacing === 'true' ? 8 : 0
        },
        900: {
          slidesPerView: 3,
          spaceBetween: this.dataset.swiperSpacing === 'true' ? 12 : 0
        },
        1200: {
          slidesPerView: 4,
          spaceBetween: this.dataset.swiperSpacing === 'true' ? 12 : 0
        },
        1500: {
          slidesPerView: 5,
          spaceBetween: this.dataset.swiperSpacing === 'true' ? 16 : 0
        },
        1800: {
          slidesPerView: 6,
          spaceBetween: this.dataset.swiperSpacing === 'true' ? 16 : 0
        }
      }
    })

    this.querySelectorAll('a[data-quick-view]').forEach(link => {
      link.addEventListener('click', (event) => {
        event.preventDefault()
        this.openQuickView(link)
      })
    })
  }

  autoplay = this.dataset.sliderAutoplay === '0'
    ? undefined
    : { delay: Number(this.dataset.sliderAutoplay) * 1000, pauseOnMouseEnter: true }

  async openQuickView (link) {
    link.disabled = true
    link.setAttribute('aria-busy', 'true')
    link.querySelector('.icon-wrapper').setAttribute('hidden', 'hidden')
    link.querySelector('.spinner-border').removeAttribute('hidden')

    const buildProductJsonUrl2 = (rawUrl) => {
      try {
        const url = new URL(rawUrl, window.location.origin)
        if (!url.pathname.endsWith('.js')) {
          url.pathname = url.pathname.replace(/\/$/, '') + '.js'
        }
        return url.toString()
      } catch (e) {
        const parts = String(rawUrl || '').split(/([?#].*)/)
        const base = (parts[0] || '').replace(/\/$/, '') + '.js'
        const tail = parts[1] || ''
        return base + tail
      }
    }

    const response = await fetch(buildProductJsonUrl2(link.dataset.productUrl))

    if (response.ok) {
      const data = await response.json()
      console.log(data)

      document.querySelector('product-quick-view-offcanvas').showModal(data)
    }

    link.disabled = false
    link.removeAttribute('aria-busy')
    link.querySelector('.icon-wrapper').removeAttribute('hidden')
    link.querySelector('.spinner-border').setAttribute('hidden', 'hidden')
  }
}
customElements.define('instagram-gallery', InstagramGallery)

class StickyATC extends HTMLElement {
  constructor () {
    super()

    this.init()
    this.listenVariantChange()
  }

  init () {
    const stickyAtc = document.querySelector('#sticky-atc')
    const top = document.querySelector('.product-main .product-block-atc').getBoundingClientRect().bottom + window.scrollY

    window.addEventListener('scroll', () => {
      if (window.scrollY > top) {
        stickyAtc.classList.add('show')
        document.body.style.paddingBottom = stickyAtc.clientHeight + 'px'
      } else {
        stickyAtc.classList.remove('show')
        document.body.style.paddingBottom = 0
      }
    })

    this.listenVariantChange()
  }

  listenVariantChange () {
    window.addEventListener('td.product.variant.change', (event) => {
      const selectedVariant = event.detail

      this.querySelector('select[name="id"]').value = selectedVariant.id
      this.querySelector('select[name="id"]').dispatchEvent(new CustomEvent('change'))
    })
  }
}
customElements.define('sticky-atc', StickyATC)

class TimelineSlider extends HTMLElement {
  constructor () {
    super()

    this.thumbs = new window.Swiper(this.querySelector('.swiper-thumbs'), {
      rewind: true,
      breakpoints: {
        0: {
          slidesPerView: 3,
          spaceBetween: 16
        },
        600: {
          slidesPerView: Number(this.dataset.totalSlides),
          spaceBetween: 32
        },
      }
    })

    this.main = new window.Swiper(this.querySelector('.swiper-main'), {
      rewind: true,
      speed: Number(this.dataset.sliderSpeed),
      autoplay: this.autoplay,
      thumbs: {
        swiper: this.thumbs,
        autoScrollOffset: 0
      }
    })

    document.addEventListener('shopify:block:select', (event) => {
      if (event.detail.sectionId === this.dataset.sectionId) {    
        this.main.slideTo(event.target.dataset.index)
      }
    })
  }

  autoplay = this.dataset.sliderAutoplay === '0'
    ? undefined
    : { delay: Number(this.dataset.sliderAutoplay) * 1000 }
}
customElements.define('timeline-slider', TimelineSlider)

class SimpleCollections extends HTMLElement {
  constructor () {
    super()

    if (window.matchMedia('(max-width: 799px)').matches) {
      setTimeout(() => {
        const left = this.querySelector('.simple-collections-item.active')?.getBoundingClientRect().left || 0
        this.scrollBy({ left, behavior: 'smooth' })
      }, 1000)
    }
  }
}
customElements.define('simple-collections', SimpleCollections)

class MarqueeImages extends HTMLElement {
  constructor () {
    super()

    this.gallery = new window.Swiper(this, {
      speed: Number(this.dataset.sliderSpeed),
      spaceBetween: 12,
      centeredSlides: true,
      autoplay: this.autoplay,
      loop: true,
      allowTouchMove: false,
      navigation: {
        prevEl: '.swiper-button-prev',
        nextEl: '.swiper-button-next'
      },
      mousewheel: {
        enabled: true,
        forceToAxis: true
      },
      breakpoints: {
        0: {
          slidesPerView: Number(this.dataset.breakpointMobile || 1),
          spaceBetween: this.dataset.swiperSpacing === 'true' ? 8 : 0
        },
        600: { 
          slidesPerView: Number(this.dataset.breakpointTablet || 2),
          spaceBetween: this.dataset.swiperSpacing === 'true' ? 8 : 0
        },
        1200: {
          slidesPerView: Number(this.dataset.breakpointDesktop || 4),
          spaceBetween: this.dataset.swiperSpacing === 'true' ? 12 : 0
        }
      }
    })
  }

  autoplay = this.dataset.sliderAutoplay === '0'
    ? undefined
    : { delay: Number(this.dataset.sliderAutoplay) * 1000, pauseOnMouseEnter: true }
}
customElements.define('marquee-images', MarqueeImages)

class FeaturedProductsTabs extends HTMLElement {
  constructor () {
    super()

    this.onTabShown = this.onTabShown.bind(this)
    this.updateOverflow = this.updateOverflow.bind(this)
  }

  connectedCallback () {
    if (this.dataset.initialized === 'true') return
    this.dataset.initialized = 'true'

    this.nav = this.querySelector('.featured-products-nav')
    if (!this.nav) return

    this.nav.addEventListener('shown.bs.tab', this.onTabShown)

    // Initial calculations
    this.updateOverflow()
    document.fonts?.ready?.then(this.updateOverflow)

    // Center initial active tab if there's overflow
    requestAnimationFrame(() => {
      this.updateOverflow()
      if (this.nav?.dataset.overflow === 'true') {
        const active = this.nav.querySelector('button[data-bs-toggle="tab"].active')
        if (active) this.centerButton(active, { smooth: false })
      }
    })

    if ('ResizeObserver' in window) {
      this.ro = new ResizeObserver(() => this.updateOverflow())
      this.ro.observe(this.nav)
    } else {
      window.addEventListener('resize', this.updateOverflow)
    }
  }

  disconnectedCallback () {
    this.nav?.removeEventListener('shown.bs.tab', this.onTabShown)
    this.ro?.disconnect?.()
    window.removeEventListener('resize', this.updateOverflow)
  }

  updateOverflow () {
    if (!this.nav) return

    // Avoid excessive layout thrash
    if (this._overflowRaf) cancelAnimationFrame(this._overflowRaf)
    this._overflowRaf = requestAnimationFrame(() => {
      const hasOverflow = this.nav.scrollWidth > this.nav.clientWidth + 1
      this.nav.dataset.overflow = hasOverflow ? 'true' : 'false'
      if (!hasOverflow) this.nav.scrollLeft = 0
    })
  }

  onTabShown (event) {
    const btn = event.target
    if (!btn?.matches?.('button[data-bs-toggle="tab"]')) return
    this.centerButton(btn, { smooth: true })
  }

  centerButton (btn, { smooth } = { smooth: true }) {
    if (!this.nav || !btn) return

    const navRect = this.nav.getBoundingClientRect()
    const btnRect = btn.getBoundingClientRect()
    const btnCenter = btnRect.left + btnRect.width / 2
    const navCenter = navRect.left + navRect.width / 2

    const targetLeft = this.nav.scrollLeft + (btnCenter - navCenter)
    const maxLeft = Math.max(0, this.nav.scrollWidth - this.nav.clientWidth)

    this.nav.scrollTo({
      left: Math.max(0, Math.min(targetLeft, maxLeft)),
      behavior: smooth ? 'smooth' : 'auto'
    })
  }
}
customElements.define('featured-products-tabs', FeaturedProductsTabs)

// Product card carousel thumbnails synchronization
function initProductCardThumbs() {
  document.querySelectorAll('.product-card-thumbs:not([data-initialized])').forEach(thumbsContainer => {
    const carouselId = thumbsContainer.dataset.carouselId
    const carousel = document.querySelector(`#${carouselId}`)
    
    if (!carousel) return
    
    // Mark as initialized to avoid duplicate listeners
    thumbsContainer.dataset.initialized = 'true'
    
    const thumbs = thumbsContainer.querySelectorAll('.product-card-thumb')
    
    // Scroll to center active thumb
    const scrollToActiveThumb = (activeThumb) => {
      if (!activeThumb || !thumbsContainer) return
      
      const containerWidth = thumbsContainer.offsetWidth
      const thumbWidth = activeThumb.offsetWidth
      const thumbOffset = activeThumb.offsetLeft
      const thumbCenter = thumbOffset + thumbWidth / 2
      const scrollPosition = thumbCenter - containerWidth / 2
      
      // Get max scroll position
      const maxScroll = thumbsContainer.scrollWidth - containerWidth
      
      thumbsContainer.scrollTo({
        left: Math.max(0, Math.min(scrollPosition, maxScroll)),
        behavior: 'smooth'
      })
    }
    
    // Update active thumb based on carousel slide
    const updateActiveThumb = () => {
      const activeCarouselItem = carousel.querySelector('.carousel-item.active')
      if (!activeCarouselItem) return
      
      const carouselItems = carousel.querySelectorAll('.carousel-item')
      const activeIndex = Array.from(carouselItems).indexOf(activeCarouselItem)
      
      let activeThumb = null
      thumbs.forEach((thumb, index) => {
        if (index === activeIndex) {
          thumb.classList.add('active')
          activeThumb = thumb
        } else {
          thumb.classList.remove('active')
        }
      })
      
      // Scroll to center the active thumb
      if (activeThumb) {
        scrollToActiveThumb(activeThumb)
      }
    }
    
    // Listen to carousel slide changes
    carousel.addEventListener('slid.bs.carousel', updateActiveThumb)
    
    // Set initial active state
    updateActiveThumb()
  })
}

// Initialize on page load
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initProductCardThumbs)
} else {
  initProductCardThumbs()
}

// Reinitialize when Shopify section loads (theme editor)
document.addEventListener('shopify:section:load', initProductCardThumbs)

